// ─── FIREBASE INIT ────────────────────────────────────────────────────────────
// Replace the values below with your Firebase project config.
// Get them from: Firebase Console → Project Settings → Your apps → Web app

import { initializeApp, getApps } from 'firebase/app';
import {
  initializeFirestore, doc, collection, getDoc, getDocs, setDoc, updateDoc,
  deleteDoc, query, where, addDoc, serverTimestamp, onSnapshot,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signOut,
  setPersistence, browserLocalPersistence, onAuthStateChanged,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { requestGCalToken, requestGCalAuthCode, SERVER_SIDE_AUTH } from './gcalAuth';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
export const db   = initializeFirestore(app, {
  experimentalForceLongPolling: true,
});
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

// ─── AUTH ─────────────────────────────────────────────────────────────────────
// Persist session in localStorage — survives browser close for up to the
// token refresh lifetime. Firebase refresh tokens don't expire by default,
// so users stay signed in for months without re-login.

export async function initPersistence() {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (e) {
    console.warn('[FOH] persistence init failed:', e.message);
  }
}

// Identity only (email/profile) — Calendar access is requested separately
// via Google Identity Services (see gcalAuth.js), decoupled from this login
// popup so it can be renewed silently in the background instead of forcing
// a full consent screen on every reconnect.
const GOOGLE_PROVIDER = new GoogleAuthProvider();
GOOGLE_PROVIDER.addScope('email');
GOOGLE_PROVIDER.addScope('profile');

const GCAL_TOKEN_KEY = 'foh_gcal_token';
// Google API access tokens from this flow last ~1hr; treat as stale a bit
// early so we hand back "needs reconnect" before a live request would 401.
const GCAL_TOKEN_MAX_AGE_MS = 55 * 60 * 1000;

function storeGCalToken(accessToken) {
  if (!accessToken) return;
  // localStorage (not sessionStorage) so the token is shared across tabs and
  // survives a page refresh — a fresh tab/reload no longer looks "stale" just
  // because it has its own empty session storage.
  localStorage.setItem(GCAL_TOKEN_KEY, JSON.stringify({ token: accessToken, issuedAt: Date.now() }));
}

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, GOOGLE_PROVIDER);
  // This credential only carries email/profile now — Calendar access is a
  // separate grant (see reconnectGCal), so nothing to cache here. The user
  // will see one "Reconnect Google Calendar" prompt right after their first
  // sign-in — accepted tradeoff, see plan notes.
  return { user: result.user };
}

// Interactive reconnect — MUST be called directly from a user click handler.
// Browsers block popups opened without a user gesture, so this is never
// invoked automatically from background data-fetch code (see getGCalToken/
// refreshGCalToken below, which fail fast instead).
export async function reconnectGCal() {
  if (SERVER_SIDE_AUTH) {
    // One consent screen, then the server holds the grant. Unlike the legacy
    // path this does NOT recur every ~55 minutes — that is the whole point.
    const code = await requestGCalAuthCode();
    await httpsCallable(functions, 'exchangeOAuthCode')({ code });
    serverToken = null;   // force the next read to vend a fresh one
    return true;
  }
  const accessToken = await requestGCalToken({ interactive: true });
  storeGCalToken(accessToken);
  return accessToken;
}

// Access tokens under server-side auth live in memory only, never
// localStorage: the server can re-vend one instantly, so persisting a
// short-lived credential across tabs buys nothing and widens the exposure.
let serverToken = null;   // { token, expiresAt }

// Distinguishes "this needs a human to click Reconnect" from a transient
// failure. functions/index.js raises exactly two such codes.
export class GCalReconsentError extends Error {
  constructor(reason) {
    super(reason === 'NOT_CONNECTED'
      ? 'Google Calendar has not been connected yet'
      : 'Google Calendar needs to be reconnected');
    this.name = 'GCalReconsentError';
    this.reason = reason;
  }
}

async function getServerGCalToken() {
  if (serverToken && serverToken.expiresAt - 60_000 > Date.now()) return serverToken.token;
  try {
    const { data } = await httpsCallable(functions, 'getCalendarToken')({});
    serverToken = { token: data.accessToken, expiresAt: Date.now() + (data.expiresIn || 3600) * 1000 };
    return serverToken.token;
  } catch (err) {
    serverToken = null;
    if (err?.code === 'functions/failed-precondition') throw new GCalReconsentError(err.message);
    // Anything else (offline, cold-start timeout) is transient — behave like
    // the legacy cache-miss and let the caller's own error handling run.
    return null;
  }
}

// Returns the cached token only if it's present and not yet stale-by-age.
// Never opens a popup — callers get a fast null instead of a hang when a
// real reconnect (reconnectGCal, from a click) is needed.
export async function getGCalToken() {
  if (SERVER_SIDE_AUTH) return getServerGCalToken();

  const raw = localStorage.getItem(GCAL_TOKEN_KEY);
  if (!raw) return null;
  try {
    const { token, issuedAt } = JSON.parse(raw);
    if (!token || Date.now() - issuedAt > GCAL_TOKEN_MAX_AGE_MS) return null;
    return token;
  } catch {
    // Legacy plain-string value from before the {token, issuedAt} format
    return raw;
  }
}

// Best-effort silent renewal — called from a background timer (see
// App.jsx), so it must never throw and never hang; requestGCalToken's
// non-interactive path already races against a hard timeout. Returns the
// fresh token on success, or null if silent renewal wasn't possible (stale
// cache is left in place; the existing reactive reconnect-banner flow
// remains the fallback).
export async function refreshGCalToken() {
  // Under server-side auth there is nothing to pre-warm: the token function
  // refreshes on demand from a refresh token that doesn't go stale. App.jsx
  // skips its polling effect entirely, so this is only reachable on the
  // legacy path.
  if (SERVER_SIDE_AUTH) return null;
  try {
    const token = await requestGCalToken({ interactive: false });
    storeGCalToken(token);
    return token;
  } catch {
    return null;
  }
}

export function watchAuthState(callback) {
  return onAuthStateChanged(auth, callback);
}

export async function firebaseSignOut() {
  serverToken = null;
  localStorage.removeItem(GCAL_TOKEN_KEY);
  sessionStorage.removeItem(GCAL_TOKEN_KEY); // clean up any pre-migration value
  return signOut(auth);
}

// ─── FIRESTORE HELPERS ───────────────────────────────────────────────────────

const FS_READ_TIMEOUT_MS = 10_000;

// Bounds Firestore reads so a connectivity hiccup fails fast with a
// retryable error instead of hanging whatever loading screen awaits it
// (e.g. App.jsx's sign-in/family-lookup bootstrap chain) forever.
function withReadTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore read timed out')), FS_READ_TIMEOUT_MS)),
  ]);
}

export async function fsGet(path) {
  const snap = await withReadTimeout(getDoc(doc(db, ...path.split('/'))));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function fsSet(path, data) {
  return setDoc(doc(db, ...path.split('/')), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

export async function fsUpdate(path, data) {
  return updateDoc(doc(db, ...path.split('/')), { ...data, updatedAt: serverTimestamp() });
}

export async function fsDelete(path) {
  return deleteDoc(doc(db, ...path.split('/')));
}

export async function fsGetCol(colPath, filters = []) {
  const colRef = collection(db, ...colPath.split('/'));
  let q = colRef;
  if (filters.length > 0) {
    const constraints = filters.map(f => where(f.field, f.op, f.value));
    q = query(colRef, ...constraints);
  }
  const snap = await withReadTimeout(getDocs(q));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function fsAdd(colPath, data) {
  const colRef = collection(db, ...colPath.split('/'));
  return addDoc(colRef, { ...data, createdAt: serverTimestamp() });
}

export function fsListen(path, callback) {
  return onSnapshot(doc(db, ...path.split('/')), snap => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// Same filter shape as fsGetCol, live instead of one-shot — for views that
// need cross-device real-time sync (e.g. the school module's assignments,
// where a parent's grading should appear on the student's screen live).
export function fsListenCol(colPath, filters, callback) {
  const colRef = collection(db, ...colPath.split('/'));
  let q = colRef;
  if (filters?.length > 0) {
    const constraints = filters.map(f => where(f.field, f.op, f.value));
    q = query(colRef, ...constraints);
  }
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
}

export async function fsLogChange(familyId, entry) {
  const colRef = collection(db, 'families', familyId, 'changelog');
  return addDoc(colRef, {
    ...entry,
    timestamp: serverTimestamp(),
  });
}
